<template>
  <section class="logic-workspace">
    <table-toolbar
      :search.sync="lgcName"
      :show-search="true"
      :show-refresh="true"
      :show-export="true"
      :show-column-settings="true"
      :columns="columnConfigOptions"
      :visible-columns.sync="visibleColumns"
      :default-columns="defaultColumnsList"
      :fixed-columns="['# ID', '逻辑名称', '操作']"
      :min-selected="4"
      search-placeholder="输入关键字搜索逻辑..."
      export-confirm-title="确定要导出当前筛选后的 Visitor 逻辑数据吗？"
      @search-change="lgcNameChange"
      @refresh="handleReset"
      @export="handleExport"
    >
      <template slot="left">
        <el-button type="primary" size="small" icon="el-icon-plus" class="btn-action" @click="handleAddLogic">新增逻辑</el-button>
      </template>

    </table-toolbar>

    <table-page
      :loading="loading"
      :empty="tables.length === 0"
      :current-page.sync="currentPage"
      :page-size.sync="pageSize"
      :total="totalCount"
      :page-sizes="[10, 20, 30, 50, 100, 200]"
      :show-top-pagination="!!totalCount"
      :show-bottom-pagination="!!totalCount"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      @retry="handleReset"
    >
      <el-table :data="tables" stripe style="width: 100%" class="premium-table">
        <el-table-column key="col-id" prop="id" width="100">
          <template slot="header">
            <span class="premium-header-title"><span class="header-icon-hash">#</span> ID</span>
          </template>
          <template slot-scope="scope">
            <span class="cell-id">{{ scope.row.id }}</span>
          </template>
        </el-table-column>

        <el-table-column key="col-name" prop="name" min-width="180">
          <template slot="header">
            <span class="premium-header-title">逻辑名称</span>
          </template>
          <template slot-scope="scope">
            <el-link type="primary" class="cell-name-link" @click="handleUpdateLogic(scope.$index, scope.row)">
              {{ scope.row.name }}
            </el-link>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('debug')" key="col-debug" prop="debug" label="调试" width="100" align="center">
          <template slot-scope="scope">
            <span :class="['status-simple-badge', scope.row.debug ? 'active' : 'inactive']">
              <span class="dot"></span>
              {{ scope.row.debug ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('timeout')" key="col-timeout" prop="timeout" label="HttpTimeout" width="150">
          <template slot-scope="scope">
            <span class="cell-text-gray">{{ scope.row.timeout }} <span class="unit-text">ms</span></span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('status')" key="col-status" prop="status" label="状态" width="120">
          <template slot-scope="scope">
            <span :class="['status-dot-badge', getLogicStatusClass(scope.row.status)]">
              <span class="dot"></span>
              {{ getLogicStatusLabel(scope.row.status) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('note')" key="col-note" prop="note" label="备注" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="cell-text">{{ scope.row.note || '-' }}</span>
          </template>
        </el-table-column>

        <el-table-column key="col-actions" width="86" align="center">
          <template slot="header">
            <span class="premium-header-title">操作</span>
          </template>
          <template slot-scope="scope">
            <el-dropdown @command="handleActionCommand($event, scope.row, scope.$index)" trigger="hover">
              <el-button size="mini" class="action-btn" circle icon="el-icon-more-outline"></el-button>
              <el-dropdown-menu slot="dropdown" class="premium-dropdown-menu">
                <el-dropdown-item command="edit">
                  <i class="el-icon-edit"></i> 编辑逻辑
                </el-dropdown-item>
                <el-dropdown-item command="delete" class="dropdown-danger">
                  <i class="el-icon-delete"></i> 删除逻辑
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
    </table-page>

    <el-drawer
      :visible.sync="dialogVisible"
      direction="rtl"
      :size="drawerSize"
      destroy-on-close
      :before-close="handleDrawerBeforeClose"
      custom-class="standard-form-drawer"
      :with-header="false"
    >
      <div class="detail-workspace">
        <div class="breadcrumb-header">
          <div class="header-top-row">
            <div class="header-left">
              <span class="header-title">
                <span class="header-title-icon"><i :class="type ? 'el-icon-edit-outline' : 'el-icon-plus'"></i></span>
                {{ type ? `编辑：${form.id} - ${form.name || ''}` : '新增逻辑' }}
              </span>
            </div>
            <div class="header-right-buttons">
              <el-button size="small" @click="handleDialogClose"><i class="el-icon-back"></i> 返回列表</el-button>
              <el-button type="success" size="small" :loading="saveLoading" :disabled="saveLoading" @click="handleSaveDialog">保存</el-button>
            </div>
          </div>
        </div>

        <div class="detail-content">
          <el-form :model="form" label-position="right" label-width="130px" size="small" :rules="rules" ref="form" class="form-container">
            <el-form-item label="逻辑 ID" prop="id">
              <el-input v-model.number="form.id" :disabled="type" placeholder="请输入逻辑 ID"></el-input>
            </el-form-item>
            <el-form-item label="逻辑名称" prop="name">
              <el-input v-model.trim="form.name" placeholder="请输入逻辑名称"></el-input>
            </el-form-item>
            <el-form-item label="HttpTimeout" prop="timeout">
              <el-input v-model.number="form.timeout" placeholder="Http Timeout (ms)"></el-input>
            </el-form-item>
            <el-form-item label="状态" prop="status">
              <el-select v-model="form.status" filterable placeholder="请选择状态" style="width: 100%">
                <el-option v-for="item in statusOptions" :key="item.value" :label="item.value + ' - ' + item.label" :value="item.value"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="调试" prop="debug" class="switch-form-item">
              <el-switch v-model="form.debug"></el-switch>
            </el-form-item>
            <el-form-item label="备注" prop="note">
              <el-input v-model.trim="form.note" type="textarea" rows="3" placeholder="请输入备注信息"></el-input>
            </el-form-item>

            <el-form-item label="自定义参数">
              <div class="args-panel">
                <el-table :data="form.args" stripe class="premium-table inline-edit-table" size="mini" max-height="280">
                  <el-table-column label="Key" min-width="130">
                    <template slot-scope="scope">
                      <div class="editable-cell">
                        <el-input v-model="scope.row.key" placeholder="Key" size="mini" @change="handleSyncLabel(scope.row)"></el-input>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="默认值" min-width="130">
                    <template slot-scope="scope">
                      <div class="editable-cell">
                        <el-input v-model="scope.row.value" placeholder="默认值" size="mini"></el-input>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="名称" min-width="130">
                    <template slot-scope="scope">
                      <div class="editable-cell">
                        <el-input v-model="scope.row.label" placeholder="名称" size="mini"></el-input>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="操作" width="68" align="center">
                    <template slot-scope="scope">
                      <el-tooltip content="删除参数" placement="top" :open-delay="500">
                        <el-button type="danger" size="mini" circle icon="el-icon-delete" plain @click="handleDeleteParams(scope.$index)"></el-button>
                      </el-tooltip>
                    </template>
                  </el-table-column>
                </el-table>
                <div class="args-action-bar">
                  <el-button size="mini" icon="el-icon-plus" class="btn-add-row" @click="handleAddParams">添加参数行</el-button>
                </div>
              </div>
            </el-form-item>
          </el-form>
        </div>
      </div>
    </el-drawer>
  </section>
</template>

<script>
import api from '@/api/channel'
import * as XLSX from 'xlsx'
import TablePage from '@/components/TablePage/index.vue'
import TableToolbar from '@/components/TableToolbar/index.vue'

export default {
  name: 'index',
  components: { TablePage, TableToolbar },
  created () {
    this.fetchData(this.pageSize, this.currentPage)
  },
  computed: {
    tables () {
      return this.tableData
    },
    drawerSize () {
      if (this.viewportWidth <= 768) return '100%'
      if (this.viewportWidth <= 1200) return '72%'
      return '56%'
    },
    columnConfigOptions () {
      return this.allConfigurableColumns.map(item => ({
        prop: item.value,
        label: item.label
      }))
    }
  },
  data () {
    const timeout = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请输入时间'))
      } else if (typeof (value) !== 'number') {
        callback(new Error('请输入数字'))
      } else if (value > 60000 || value < 1000) {
        callback(new Error('时间限制为 1000ms - 60000ms'))
      } else {
        callback()
      }
    }
    return {
      viewportWidth: window.innerWidth,
      loading: false,
      saveLoading: false,
      pageSize: 20,
      currentPage: 1,
      totalCount: 0,
      tableData: [],
      dialogVisible: false,
      lgcName: '',
      visibleColumns: ['debug', 'timeout', 'status', 'note'],
      defaultColumnsList: ['debug', 'timeout', 'status', 'note'],
      allConfigurableColumns: [
        { value: 'debug', label: '调试' },
        { value: 'timeout', label: 'HttpTimeout' },
        { value: 'status', label: '状态' },
        { value: 'note', label: '备注' }
      ],
      form: {
        args: [{ label: '', key: '', value: '' }],
        status: 0,
        note: ''
      },
      statusOptions: [
        { label: '正常', value: 0 },
        { label: '废弃', value: 1 }
      ],
      type: false,
      index: 0,
      rules: {
        id: [{ type: 'number', required: true, message: '请输入数字，且不能为空', trigger: 'change' }],
        name: [{ type: 'string', required: true, message: '请输入名称', trigger: 'blur' }],
        timeout: [{ type: 'number', validator: timeout, trigger: 'blur' }],
        debug: [{ type: 'boolean', required: true, message: 'debug不能为空', trigger: 'change' }],
        status: [{ type: 'number', required: true, message: '请选择状态', trigger: 'change' }],
        note: [{ type: 'string', required: false, message: '请输入备注', trigger: 'blur' }]
      }
    }
  },
  mounted () {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize () {
      this.viewportWidth = window.innerWidth
    },
    fetchData (pageSize, currentPage) {
      this.loading = true
      const params = {
        size: pageSize,
        page: currentPage,
        name: this.lgcName
      }
      api.getLogicDataByPage(params).then(res => {
        this.tableData = res.data
        this.totalCount = res.total
      }).finally(() => {
        this.loading = false
      })
    },
    handleSizeChange (val) {
      this.pageSize = val
      this.currentPage = 1
      this.fetchData(val, this.currentPage)
    },
    handleCurrentChange (val) {
      this.currentPage = val
      this.fetchData(this.pageSize, val)
    },
    lgcNameChange (val) {
      this.lgcName = val
      this.currentPage = 1
      this.fetchData(this.pageSize, this.currentPage)
    },
    handleReset () {
      this.fetchData(this.pageSize, this.currentPage)
    },
    handleExport () {
      if (!this.tables || this.tables.length === 0) {
        this.$message.warning('当前暂无可导出的 Visitor 逻辑数据')
        return
      }
      const exportData = this.tables.map(row => {
        return {
          逻辑ID: row.id,
          逻辑名称: row.name,
          调试: row.debug ? '开启' : '关闭',
          HttpTimeout: row.timeout,
          状态: this.getLogicStatusLabel(row.status),
          备注: row.note || ''
        }
      })
      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(exportData)
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Visitor逻辑')
      XLSX.writeFile(workbook, `Visitor逻辑-${new Date().getTime()}.xlsx`)
      this.$message.success('数据导出成功')
    },
    handleAddLogic () {
      this.type = false
      const id = this.tableData.length ? this.tableData[this.tableData.length - 1].id : 0
      this.form = {
        id: id + 1,
        debug: true,
        timeout: 3000,
        args: [{ label: '', key: '', value: '' }],
        status: 0,
        note: ''
      }
      this.dialogVisible = true
    },
    handleUpdateLogic (index, rowData) {
      this.type = true
      this.index = index
      const data = Object.assign({}, rowData)
      data.args = data.args ? JSON.parse(data.args) : []
      this.form = data
      this.dialogVisible = true
    },
    handleSyncLabel (item) {
      item.label = item.key
    },
    handleSaveDialog () {
      if (this.saveLoading) return
      this.$refs.form.validate((valid) => {
        if (!valid) return false
        this.saveLoading = true
        const params = Object.assign({}, this.form)
        if (params.args) {
          let len = params.args.length
          const arr = params.args
          while (len--) {
            if (arr[len].key === '') {
              arr.splice(len, 1)
            }
          }
        }
        params.args = params.args && params.args.length > 0 ? JSON.stringify(params.args) : null
        const f = this.type ? api.changeLogic : api.addLogic
        f(params).then(res => {
          if (this.type) {
            this.tableData.splice(this.index, 1, res)
          } else {
            this.tableData.push(res)
          }
          this.$message.success(this.type ? '修改成功' : '添加成功')
          this.handleDialogClose()
        }).finally(() => {
          this.saveLoading = false
        })
      })
    },
    handleDeleteLogic (id, i) {
      this.$confirm('确定删除吗?', '提示', { type: 'warning' }).then(() => {
        api.deleteLogic(id).then(() => {
          this.tableData.splice(i, 1)
          this.$message.success('删除成功')
        })
      })
    },
    handleDialogClose () {
      this.dialogVisible = false
      if (this.$refs.form) {
        this.$refs.form.clearValidate()
      }
    },
    handleDrawerBeforeClose (done) {
      this.handleDialogClose()
      done()
    },
    handleAddParams () {
      const params = { label: '', key: '', value: '' }
      if (!this.form.args) {
        this.$set(this.form, 'args', [])
      }
      this.form.args.push(params)
    },
    handleDeleteParams (i) {
      this.form.args.splice(i, 1)
    },
    handleActionCommand (command, row, index) {
      if (command === 'edit') {
        this.handleUpdateLogic(index, row)
      } else if (command === 'delete') {
        this.handleDeleteLogic(row.id, index)
      }
    },
    getLogicStatusLabel (status) {
      if (status === 0) return '正常'
      if (status === 1) return '废弃'
      return '未知'
    },
    getLogicStatusClass (status) {
      if (status === 0) return 'online'
      if (status === 1) return 'closed'
      return 'developing'
    }
  }
}
</script>

<style scoped>
.logic-workspace {
  padding: 16px 24px;
}

.btn-action {
  height: 32px;
  padding: 0 16px;
  border-color: var(--primary-color) !important;
  border-radius: 8px !important;
  background-color: var(--primary-color) !important;
  font-weight: 600 !important;
}

.btn-action:hover {
  border-color: var(--primary-hover) !important;
  background-color: var(--primary-hover) !important;
}

.premium-table {
  border-radius: 8px;
  overflow: hidden;
  border: none !important;
  font-size: 13px !important;
  color: var(--text-main) !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif !important;
}

.premium-table::before {
  display: none !important;
}

.premium-table :deep(.el-table__header-wrapper) th,
.premium-table :deep(.el-table__fixed-header-wrapper) th,
.premium-table :deep(.el-table__fixed-right-header-wrapper) th,
.premium-table :deep(th.el-table__fixed-column) {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
  font-weight: 700 !important;
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-color) !important;
}

.premium-table :deep(.el-table__row) td {
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-light) !important;
}

.premium-table :deep(.el-table__row) {
  transition: background-color 0.2s ease;
}

.premium-table :deep(.el-table__row:hover) td {
  background-color: var(--bg-hover) !important;
}

.premium-table :deep(.el-table__body tr.hover-row > td),
.premium-table :deep(.el-table__body tr.hover-row.current-row > td),
.premium-table :deep(.el-table__body tr.hover-row.el-table__row--striped > td) {
  background-color: var(--bg-hover) !important;
}

.premium-header-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: inherit;
}

.header-icon-hash {
  font-family: Menlo, Monaco, Consolas, monospace;
  font-size: 13.5px;
  font-weight: 900;
  color: inherit;
}

.cell-id,
.cell-text-gray {
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace !important;
  font-size: 12.5px !important;
}

.cell-id {
  font-weight: 600;
  color: var(--text-muted);
}

.cell-text {
  color: var(--text-main);
  font-weight: 500;
}

.cell-text-gray {
  color: var(--text-muted);
  font-weight: 500;
}

.cell-name-link {
  font-size: 13.5px !important;
  font-weight: 700 !important;
  text-decoration: none !important;
}

.action-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
}

.action-btn:hover {
  border-color: #cbd5e1;
  background: #f1f5f9;
  color: var(--text-main);
}

.dropdown-danger,
.dropdown-danger i {
  color: #ef4444 !important;
}

.status-dot-badge {
  min-width: 64px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-dot-badge .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-dot-badge.online {
  background-color: rgba(34, 197, 94, 0.08);
  color: #16a34a;
}

.status-dot-badge.online .dot {
  background-color: #22c55e;
}

.status-dot-badge.closed {
  background-color: rgba(239, 68, 68, 0.08);
  color: #dc2626;
}

.status-dot-badge.closed .dot {
  background-color: #ef4444;
}

.status-dot-badge.developing {
  background-color: rgba(100, 116, 139, 0.08);
  color: #64748b;
}

.status-dot-badge.developing .dot {
  background-color: #94a3b8;
}

.detail-workspace {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 !important;
  background-color: #f8fafc;
}

.breadcrumb-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box;
  padding: 12px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}

.header-top-row,
.header-left,
.header-right-buttons,
.header-title {
  display: flex;
  align-items: center;
}

.header-top-row {
  width: 100%;
  justify-content: space-between;
  gap: 16px;
}

.header-left {
  min-width: 0;
  gap: 12px;
}

.header-right-buttons {
  flex-shrink: 0;
  gap: 8px;
  margin-left: auto;
}

.header-title {
  min-width: 0;
  gap: 8px;
  color: #1e293b;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
}

.header-title-icon {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: rgba(37, 99, 235, 0.08);
  color: var(--primary-color);
}

.detail-content {
  flex: 1;
  overflow-y: auto;
  margin: 12px;
  padding: 18px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.form-container {
  width: 100%;
}

.form-container :deep(.el-form-item) {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.form-container :deep(.el-form-item__label) {
  width: 25% !important;
  min-width: 112px;
  max-width: 180px;
  flex: 0 0 25%;
  padding-right: 12px;
  color: #475569;
  font-weight: 600;
  line-height: 32px;
  text-align: left;
}

.form-container :deep(.el-form-item__content) {
  min-width: 0;
  flex: 1 1 75%;
  margin-left: 0 !important;
}

.form-container :deep(.el-select) {
  width: 100%;
}

.switch-form-item :deep(.el-form-item__content) {
  min-height: 32px;
  display: flex;
  align-items: center;
}

.inline-edit-table :deep(.el-input__inner) {
  height: 28px;
  border-color: transparent;
  border-radius: 6px;
  background: transparent;
  line-height: 28px;
}

.inline-edit-table :deep(.el-table__row:hover .el-input__inner) {
  border-color: #cbd5e1;
  background: #ffffff;
}

.inline-edit-table :deep(.el-input__inner:focus) {
  border-color: var(--primary-color);
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.unit-text {
  color: #94a3b8;
  font-size: 12px;
}

.args-panel {
  width: 100%;
  min-width: 0;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}

.args-action-bar {
  display: flex;
  justify-content: flex-start;
  padding: 10px 12px;
  border-top: 1px solid #F1F5F9;
  background: #F8FAFC;
}

.btn-add-row {
  border-radius: 8px !important;
  font-weight: 600 !important;
}

/* 深色模式适配 */
html.dark .detail-workspace {
  background-color: var(--bg-page) !important;
}

html.dark .breadcrumb-header {
  background-color: var(--bg-card) !important;
  border-bottom-color: var(--border-color) !important;
}

html.dark .breadcrumb-header .header-title {
  color: var(--text-main) !important;
}

html.dark .detail-content {
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  box-shadow: none !important;
}

html.dark .form-container :deep(.el-form-item__label) {
  color: var(--text-muted) !important;
}

html.dark .args-panel {
  border-color: var(--border-color) !important;
  background-color: var(--bg-card) !important;
}

html.dark .args-action-bar {
  border-top-color: var(--border-color) !important;
  background-color: var(--bg-page) !important;
}

html.dark .btn-add-row {
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  color: var(--text-main) !important;
}

html.dark .btn-add-row:hover {
  background-color: var(--bg-hover) !important;
  border-color: var(--border-hover) !important;
}

html.dark .inline-edit-table :deep(.el-input__inner) {
  color: var(--text-main) !important;
}

html.dark .inline-edit-table :deep(.el-table__row:hover .el-input__inner) {
  border-color: var(--border-color) !important;
  background-color: var(--bg-page) !important;
}

html.dark .inline-edit-table :deep(.el-input__inner:focus) {
  border-color: var(--primary-color) !important;
  background-color: var(--bg-page) !important;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2) !important;
}

html.dark .status-dot-badge.online {
  background-color: rgba(34, 197, 94, 0.15) !important;
  color: #4ade80 !important;
}

html.dark .status-dot-badge.closed {
  background-color: rgba(239, 68, 68, 0.15) !important;
  color: #f87171 !important;
}

html.dark .status-dot-badge.developing {
  background-color: rgba(148, 163, 184, 0.15) !important;
  color: #94a3b8 !important;
}

@media (max-width: 1200px) {
  .logic-workspace {
    padding: 16px;
  }
}

@media (max-width: 768px) {
  .breadcrumb-header {
    padding: 12px 16px;
  }

  .header-top-row,
  .header-left {
    align-items: flex-start;
    flex-direction: column;
  }

  .form-container :deep(.el-form-item) {
    display: block;
  }

  .form-container :deep(.el-form-item__label) {
    width: 100% !important;
    max-width: none;
    padding-right: 0;
  }
}
</style>
